import React, { FC, useMemo } from 'react';

import { UseQueryResult } from '@tanstack/react-query';

import { EvSection, SORT_ORDER, TableSortOption } from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { ErrorView } from 'src/apps/dashboard/components/error-view/ErrorView';
import { getMobileRunsColumns } from 'src/apps/dashboard/components/mobile-runs-table/MobileRunsTableColumns';
import { DEFAULT_DATE_RANGE_DAYS } from 'src/apps/dashboard/constants/DashboardFiltersDefinitions';
import { ERROR_TYPE } from 'src/apps/dashboard/types/chartErrors';
import { TMfaSessionsResult } from 'src/apps/dashboard/types/MobileDashboardServiceTypes';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { isQueryLoading } from 'src/common/helpers/QueryHelpers';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

type UseRunsQuery = (params: {
	filters: IFilterOptions;
	dateRange: string;
	sort: TableSortOption;
	page: number;
	pageSize: number;
}) => [UseQueryResult<TMfaSessionsResult>, () => void];

interface IBusinessUnitMobileRunsTableProps {
	dashboardFilters: IFilterOptions;
	unitIdLabel?: string;
	tableId: string;
	nameLabel: string;
	useRunsQuery: UseRunsQuery;
}

const DEFAULT_SORT: TableSortOption = { dataField: 'date', order: SORT_ORDER.DESC };

export const BusinessUnitMobileRunsTable: FC<IBusinessUnitMobileRunsTableProps> = ({
	dashboardFilters,
	unitIdLabel,
	tableId,
	nameLabel,
	useRunsQuery
}) => {
	const { getTableStateById } = useTableState();
	const { tableSort, paginationInfo } = getTableStateById(tableId);

	const testPeriod = dashboardFilters?.dateRange
		? String(dashboardFilters.dateRange[0].value)
		: String(DEFAULT_DATE_RANGE_DAYS);
	const customFieldFilters: IFilterOptions = omit(dashboardFilters ?? {}, ['dateRange']);
	customFieldFilters.unitId = [{ label: unitIdLabel, value: unitIdLabel }];

	const [runsQuery] = useRunsQuery({
		filters: customFieldFilters,
		dateRange: testPeriod,
		sort: tableSort?.[0] ?? DEFAULT_SORT,
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage
	});

	const total = runsQuery.data?.total ?? 0;
	const unfilteredTotal = runsQuery.data?.unfilteredTotal ?? 0;
	const rows = runsQuery.data?.rows ?? [];
	const columns = useMemo(() => getMobileRunsColumns(nameLabel), [nameLabel]);

	const isTableLoading = isQueryLoading(runsQuery);

	const tableOptions = useMemo(
		() => ({
			keyField: 'id',
			title: nameLabel,
			caption: `${nameLabel} table`,
			pagination: true,
			dataType: 'runs',
			remote: true,
			defaultSorted: [DEFAULT_SORT]
		}),
		[nameLabel]
	);

	if (isTableLoading) {
		return (
			<EvSection ariaLabel={`${nameLabel} table`}>
				<TableSkeleton columns={columns.length} rowCount={5} />
			</EvSection>
		);
	}

	if (runsQuery.isError || (rows.length === 0 && !isTableLoading)) {
		const errorType = runsQuery.isError ? ERROR_TYPE.FAILED : ERROR_TYPE.NO_DATA;
		return <ErrorView errorType={errorType} />;
	}

	return (
		<EvSection ariaLabel={`${nameLabel} table`}>
			<PlatformTable
				isTableLoading={isTableLoading}
				tableId={tableId}
				tableData={rows}
				totalPaginationableResults={total}
				totalCount={unfilteredTotal}
				columns={columns}
				options={tableOptions}
			/>
		</EvSection>
	);
};
